import first from 'lodash/first';
import last from 'lodash/last';
import uniq from 'lodash/uniq';
import { RailJourneyIdentifier } from '../../../types/api/v2/obt/model/rail-journey-identifier';
import { RailDeliveryOption } from '../../../types/api/v2/obt/model/rail-delivery-option';
import { defineCommonMessage } from '../../../translations/defineMessage';
import getDateTimeDiff from '../../../date-utils/getDateTimeDiff';
import minutesToDurationString from '../../../date-utils/minutesToDurationString';
import { ETicketMetadataTypeEnum } from '../../../types/api/v2/obt/model/eticket-metadata';
import type { Rail } from '../../../types/api/v2/obt/model/rail';
import { RailSearchType } from '../../../types/api/v2/obt/model/rail-search-type';
import type { RailAppliedPromotion } from '../../../types/api/v2/obt/model/rail-applied-promotion';
import { getNameStringFromName } from '../../../utils/common';
import { MoneyUtil } from '../../../utils/Money';
import type { PnrV3ManagerProps } from '../PnrV3Manager';
import { PnrV3Manager } from '../PnrV3Manager';
import type { Fare } from './types';
import type { Co2EmissionDetails } from '../../../types/api/v2/obt/model/co2-emission-details';

export class RailPnrV3Manager extends PnrV3Manager {
  railPnr?: Rail;

  constructor({ pnrData, pnrId }: PnrV3ManagerProps) {
    super({ pnrData, pnrId });
    this.railPnr = this.pnrData.railPnr;
  }

  public railDetails(options?: { isPnrSplit?: boolean }) {
    const { isPnrSplit = false } = options || {};
    // When a rail PNR is split into outward and inward journeys,
    // only one journey segment (either outward or inward) will be populated at a time.
    const legs = this.railPnr?.legInfos ?? [];
    const firstLeg = first(legs);
    const lastLeg = last(legs);
    const journeyId = this.railPnr?.inwardJourney ? RailJourneyIdentifier.Inward : RailJourneyIdentifier.Outward;

    const isOpenReturn = this.railPnr?.type === RailSearchType.OpenReturn;

    const outwardJourneyInfo = {
      originName: '',
      destinationName: '',
      duration: '',
      legIndices: [] as number[],
      co2EmissionDetails: undefined as Co2EmissionDetails | undefined,
    };

    let outwardJourneyDurationInMinutes = 0;

    const outwardJourneyLegs = this.railPnr?.outwardJourney.legs ?? [];
    if (outwardJourneyLegs.length > 0) {
      const firstLegIndex = isPnrSplit ? 0 : outwardJourneyLegs[0];
      const lastLegIndex = isPnrSplit ? legs.length - 1 : outwardJourneyLegs[outwardJourneyLegs.length - 1];
      const outwardJourneyFirstLeg = this.railPnr?.legInfos[firstLegIndex];
      const outwardJourneyLastLeg = this.railPnr?.legInfos[lastLegIndex];

      outwardJourneyInfo.originName = outwardJourneyFirstLeg?.originInfo?.name ?? '';
      outwardJourneyInfo.destinationName = outwardJourneyLastLeg?.destinationInfo?.name ?? '';
      outwardJourneyDurationInMinutes = getDateTimeDiff(
        outwardJourneyLastLeg?.arriveAtLocal?.iso8601 ?? '',
        outwardJourneyFirstLeg?.departAtLocal?.iso8601 ?? '',
      );
      outwardJourneyInfo.duration = minutesToDurationString(outwardJourneyDurationInMinutes);
      outwardJourneyInfo.legIndices = this.railPnr?.outwardJourney.legs ?? [];
      outwardJourneyInfo.co2EmissionDetails = this.railPnr?.outwardJourney.co2EmissionDetails;
    }

    const inwardJourneyInfo = {
      originName: '',
      destinationName: '',
      duration: '',
      legIndices: [] as number[],
      co2EmissionDetails: undefined as Co2EmissionDetails | undefined,
    };

    let inwardJourneyDurationInMinutes = 0;

    if (this.railPnr?.inwardJourney) {
      const inwardJourneyLegs = this.railPnr?.inwardJourney.legs ?? [];

      const firstLegIndex = isPnrSplit ? 0 : inwardJourneyLegs[0];
      const lastLegIndex = isPnrSplit ? legs.length - 1 : inwardJourneyLegs[inwardJourneyLegs.length - 1];
      const inwardJourneyFirstLeg = this.railPnr?.legInfos[firstLegIndex];
      const inwardJourneyLastLeg = this.railPnr?.legInfos[lastLegIndex];

      inwardJourneyInfo.originName = inwardJourneyFirstLeg?.originInfo?.name ?? '';
      inwardJourneyInfo.destinationName = inwardJourneyLastLeg?.destinationInfo?.name ?? '';
      inwardJourneyDurationInMinutes = getDateTimeDiff(
        inwardJourneyLastLeg?.arriveAtLocal?.iso8601 ?? '',
        inwardJourneyFirstLeg?.departAtLocal?.iso8601 ?? '',
      );
      inwardJourneyInfo.duration = minutesToDurationString(inwardJourneyDurationInMinutes);
      inwardJourneyInfo.legIndices = this.railPnr?.inwardJourney?.legs ?? [];
      inwardJourneyInfo.co2EmissionDetails = this.railPnr?.inwardJourney.co2EmissionDetails;
    }

    const hasInwardJourney = !!this.railPnr?.inwardJourney;

    const railDetails = legs.map((leg, legIdx) => {
      const passengers = this.pnrData?.pnrTravelers?.map((pnrTraveler, index) => ({
        name: pnrTraveler.personalInfo?.name,
        id: pnrTraveler?.userId?.id || `additional-passenger-${index}`,
        isAdditionalTraveler: !pnrTraveler?.userId?.id,
        index,
      }));

      const passengerDetails =
        passengers?.map((passenger) => {
          const requiredTravelerInfo = leg.travelerRailInfo?.find(
            (travelerInfo) => passenger.id === travelerInfo.userId?.id,
          );

          const requiredAllocatedSpace = passenger.isAdditionalTraveler
            ? leg.allocatedSpaces?.[passenger.index]
            : leg.allocatedSpaces?.find((space) => space.userId?.id === passenger?.id);
          return {
            name: getNameStringFromName(passenger?.name),
            preferredName: getNameStringFromName(passenger?.name, { usePreferredName: true }),
            loyaltyNumber: requiredTravelerInfo?.loyaltyDetails?.loyaltyNumber,
            loyaltyIssuedBy: requiredTravelerInfo?.loyaltyDetails?.loyaltyProgram,
            cardNumber: requiredTravelerInfo?.railCard?.cardNumber,
            cardName: requiredTravelerInfo?.railCard?.name,
            seatNumber: requiredAllocatedSpace?.seatNumber,
            coachNumber: requiredAllocatedSpace?.coachNumber,
            additionalProperties: requiredAllocatedSpace?.additionalProperties ?? [],
          };
        }) ?? [];

      const layover =
        legIdx < legs.length - 1
          ? {
              code: legs[legIdx + 1].originInfo?.sourceRefInfos?.find((info) => info.inventoryName === 'Amtrak')
                ? legs[legIdx + 1].originInfo?.code
                : '',
              city: legs[legIdx + 1].originInfo?.cityName ?? '',
              duration: minutesToDurationString(
                getDateTimeDiff(legs[legIdx + 1].departAtLocal?.iso8601 ?? '', leg?.arriveAtLocal?.iso8601 ?? ''),
              ),
              isTrainChange: leg.vehicle.carrierName !== legs[legIdx + 1].vehicle.carrierName,
            }
          : undefined;

      const co2EmissionsInKg =
        leg.co2EmissionGramsPerPassenger && leg.co2EmissionGramsPerPassenger > 0
          ? leg.co2EmissionGramsPerPassenger / 1000
          : 0;
      const collectionReference =
        this.railPnr?.ticketDetails &&
        this.railPnr.ticketDetails.length > legIdx &&
        this.railPnr.ticketDetails[legIdx]?.deliveryOption === RailDeliveryOption.Kiosk
          ? this.railPnr.ticketDetails[legIdx].reference
          : undefined;

      const carrierConfirmation = first(first(this.railPnr?.sections)?.vendorConfirmations)?.vendorConfirmationId || '';
      const coachSeatCombination: string[] = uniq(
        passengerDetails
          .filter((passenger) => passenger.coachNumber && passenger.seatNumber)
          .map((passenger) => `${passenger.coachNumber} ${passenger.seatNumber}`),
      ).sort();
      const railcardNames: string[] = uniq(
        passengerDetails
          .map((passenger) => passenger.cardName)
          .filter((cardName): cardName is string => cardName !== undefined),
      );

      return {
        legId: leg.legId,

        railwayDetails: {
          vehicle: leg.vehicle,
          // @deprecated - use from vehicle
          carrierName: leg.vehicle.carrierName,
          // @deprecated - use from vehicle
          timetableId: leg.vehicle.timetableId,
          vehicleType: leg.vehicle.type,
          carrierConfirmation,
          bookingId: this.pnrId,
          fareType: leg.fareType,
          railFareType: leg.railFareType,
          travelClass: leg.travelClass,
          railCard: railcardNames.length > 0 ? railcardNames.join(', ') : undefined,
          co2Emissions: co2EmissionsInKg,
          collectionReference,
          sourceConfirmation: this.pnrData.sourceInfo?.sourcePnrId,
          seatsWithCoaches: coachSeatCombination.length > 0 ? coachSeatCombination.join(', ') : undefined,
        },
        origin: {
          date: leg?.departAtLocal,
          stationCode: leg.originInfo?.sourceRefInfos?.find((info) => info.inventoryName === 'Amtrak')
            ? leg.originInfo?.code
            : '',
          city: leg.originInfo?.cityName,
          name: leg.originInfo?.name,
          countryCode: leg.originInfo?.countryCode,
        },
        destination: {
          date: leg?.arriveAtLocal,
          stationCode: leg.destinationInfo?.sourceRefInfos?.find((info) => info.inventoryName === 'Amtrak')
            ? leg.destinationInfo?.code
            : '',
          city: leg.destinationInfo?.cityName,
          name: leg.destinationInfo?.name,
          countryCode: leg.destinationInfo?.countryCode,
        },
        duration: minutesToDurationString(
          getDateTimeDiff(leg?.arriveAtLocal?.iso8601 ?? '', leg?.departAtLocal?.iso8601 ?? ''),
        ),
        layover,
        passengerDetails,
        vendorName: leg.vendorName,
        isOutOfPolicy: this.isPnrOutOfPolicy(),
      };
    });

    let totalDuration = '';
    if (isPnrSplit) {
      totalDuration = hasInwardJourney ? inwardJourneyInfo.duration : outwardJourneyInfo.duration;
    } else {
      totalDuration = minutesToDurationString(inwardJourneyDurationInMinutes + outwardJourneyDurationInMinutes);
    }

    const travelerDetails = super.travelerDetails();

    const additionalTravelerDetails = this?.pnrData?.railPnr?.passengerInfos?.filter(
      (passenger) => !passenger?.userOrgId?.userId?.id,
    );

    const totalCo2Emissions = this.getTotalCo2EmissionsInKg();
    return {
      startDate: firstLeg?.departAtLocal,
      endDate: lastLeg?.arriveAtLocal,
      railDetails,
      travelerDetails,
      additionalTravelerDetails,
      sourceConfirmation: this.pnrData.sourceInfo?.sourcePnrId,
      isOpenReturn,
      outwardJourneyInfo,
      inwardJourneyInfo,
      hasInwardJourney,
      duration: totalDuration,
      totalCo2Emissions,
      bookingStatus: this.pnrData.bookingStatus,
      journeyId,
    };
  }

  public railDetailsAfterSplit() {
    return this.railDetails({ isPnrSplit: true });
  }

  public previousRailDetails() {
    const previousItinerary = this.railPnr?.previousItinerary;
    const exchangeInfo = this.railPnr?.exchangeInfo;
    const legs = this.railPnr?.previousItinerary?.legInfos ?? [];
    const previousOutwardJourneyInfo = {
      originName: '',
      destinationName: '',
      duration: '',
      legIndices: [] as number[],
      co2EmissionDetails: undefined as Co2EmissionDetails | undefined,
      startDate: '',
      endDate: '',
    };

    let outwardJourneyDurationInMinutes = 0;

    const outwardJourneyLegs = previousItinerary?.outwardJourney.legs ?? [];
    if (outwardJourneyLegs.length > 0) {
      const firstLegIndex = outwardJourneyLegs[0];
      const lastLegIndex = outwardJourneyLegs[outwardJourneyLegs.length - 1];
      const outwardJourneyFirstLeg = previousItinerary?.legInfos[firstLegIndex];
      const outwardJourneyLastLeg = previousItinerary?.legInfos[lastLegIndex];

      previousOutwardJourneyInfo.originName = outwardJourneyFirstLeg?.originInfo?.name ?? '';
      previousOutwardJourneyInfo.destinationName = outwardJourneyLastLeg?.destinationInfo?.name ?? '';
      outwardJourneyDurationInMinutes = getDateTimeDiff(
        outwardJourneyLastLeg?.arriveAtLocal?.iso8601 ?? '',
        outwardJourneyFirstLeg?.departAtLocal?.iso8601 ?? '',
      );
      previousOutwardJourneyInfo.duration = minutesToDurationString(outwardJourneyDurationInMinutes);
      previousOutwardJourneyInfo.legIndices = previousItinerary?.outwardJourney.legs ?? [];
      previousOutwardJourneyInfo.co2EmissionDetails = previousItinerary?.outwardJourney.co2EmissionDetails;
      previousOutwardJourneyInfo.startDate = outwardJourneyFirstLeg?.departAtLocal?.iso8601 ?? '';
      previousOutwardJourneyInfo.endDate = outwardJourneyLastLeg?.arriveAtLocal?.iso8601 ?? '';
    }

    const previousInwardJourneyInfo = {
      originName: '',
      destinationName: '',
      duration: '',
      legIndices: [] as number[],
      co2EmissionDetails: undefined as Co2EmissionDetails | undefined,
      startDate: '',
      endDate: '',
    };

    let inwardJourneyDurationInMinutes = 0;

    if (previousItinerary?.inwardJourney) {
      const inwardJourneyLegs = previousItinerary?.inwardJourney.legs ?? [];

      const firstLegIndex = inwardJourneyLegs[0];
      const lastLegIndex = inwardJourneyLegs[inwardJourneyLegs.length - 1];
      const inwardJourneyFirstLeg = previousItinerary?.legInfos[firstLegIndex];
      const inwardJourneyLastLeg = previousItinerary?.legInfos[lastLegIndex];

      previousInwardJourneyInfo.originName = inwardJourneyFirstLeg?.originInfo?.name ?? '';
      previousInwardJourneyInfo.destinationName = inwardJourneyLastLeg?.destinationInfo?.name ?? '';
      inwardJourneyDurationInMinutes = getDateTimeDiff(
        inwardJourneyLastLeg?.arriveAtLocal?.iso8601 ?? '',
        inwardJourneyFirstLeg?.departAtLocal?.iso8601 ?? '',
      );
      previousInwardJourneyInfo.duration = minutesToDurationString(inwardJourneyDurationInMinutes);
      previousInwardJourneyInfo.legIndices = previousItinerary?.inwardJourney?.legs ?? [];
      previousInwardJourneyInfo.co2EmissionDetails = previousItinerary?.inwardJourney.co2EmissionDetails;
      previousInwardJourneyInfo.startDate = inwardJourneyFirstLeg?.departAtLocal?.iso8601 ?? '';
      previousInwardJourneyInfo.endDate = inwardJourneyLastLeg?.arriveAtLocal?.iso8601 ?? '';
    }

    const previousRailDetails = legs.map((leg, legIdx) => {
      const passengers = this.pnrData?.pnrTravelers?.map((pnrTraveler, index) => ({
        name: pnrTraveler.personalInfo?.name,
        id: pnrTraveler?.userId?.id || `additional-passenger-${index}`,
        isAdditionalTraveler: !pnrTraveler?.userId?.id,
        index,
      }));

      const passengerDetails =
        passengers?.map((passenger) => {
          const requiredTravelerInfo = leg.travelerRailInfo?.find(
            (travelerInfo) => passenger.id === travelerInfo.userId?.id,
          );

          const requiredAllocatedSpace = passenger.isAdditionalTraveler
            ? leg.allocatedSpaces?.[passenger.index]
            : leg.allocatedSpaces?.find((space) => space.userId?.id === passenger?.id);
          return {
            name: getNameStringFromName(passenger?.name),
            preferredName: getNameStringFromName(passenger?.name, { usePreferredName: true }),
            loyaltyNumber: requiredTravelerInfo?.loyaltyDetails?.loyaltyNumber,
            loyaltyIssuedBy: requiredTravelerInfo?.loyaltyDetails?.loyaltyProgram,
            cardNumber: requiredTravelerInfo?.railCard?.cardNumber,
            cardName: requiredTravelerInfo?.railCard?.name,
            seatNumber: requiredAllocatedSpace?.seatNumber,
            coachNumber: requiredAllocatedSpace?.coachNumber,
            additionalProperties: requiredAllocatedSpace?.additionalProperties ?? [],
          };
        }) ?? [];

      const layover =
        legIdx < legs.length - 1
          ? {
              code: legs[legIdx + 1].originInfo?.sourceRefInfos?.find((info) => info.inventoryName === 'Amtrak')
                ? legs[legIdx + 1].originInfo?.code
                : '',
              city: legs[legIdx + 1].originInfo?.cityName ?? '',
              duration: minutesToDurationString(
                getDateTimeDiff(legs[legIdx + 1].departAtLocal?.iso8601 ?? '', leg?.arriveAtLocal?.iso8601 ?? ''),
              ),
              isTrainChange: leg.vehicle.carrierName !== legs[legIdx + 1].vehicle.carrierName,
            }
          : undefined;

      const co2EmissionsInKg =
        leg.co2EmissionGramsPerPassenger && leg.co2EmissionGramsPerPassenger > 0
          ? leg.co2EmissionGramsPerPassenger / 1000
          : 0;
      const collectionReference =
        this.railPnr?.ticketDetails &&
        this.railPnr.ticketDetails.length > legIdx &&
        this.railPnr.ticketDetails[legIdx]?.deliveryOption === RailDeliveryOption.Kiosk
          ? this.railPnr.ticketDetails[legIdx].reference
          : undefined;

      const carrierConfirmation = first(first(this.railPnr?.sections)?.vendorConfirmations)?.vendorConfirmationId || '';
      const coachSeatCombination: string[] = uniq(
        passengerDetails
          .filter((passenger) => passenger.coachNumber && passenger.seatNumber)
          .map((passenger) => `${passenger.coachNumber} ${passenger.seatNumber}`),
      ).sort();
      const railcardNames: string[] = uniq(
        passengerDetails
          .map((passenger) => passenger.cardName)
          .filter((cardName): cardName is string => cardName !== undefined),
      );

      return {
        legId: leg.legId,

        railwayDetails: {
          carrierName: leg.vehicle.carrierName,
          timetableId: leg.vehicle.timetableId,
          carrierConfirmation,
          bookingId: this.pnrId,
          fareType: leg.fareType,
          travelClass: leg.travelClass,
          railCard: railcardNames.length > 0 ? railcardNames.join(', ') : undefined,
          co2Emissions: co2EmissionsInKg,
          collectionReference,
          sourceConfirmation: this.pnrData.sourceInfo?.sourcePnrId,
          vehicleType: leg.vehicle.type,
          seatsWithCoaches: coachSeatCombination.length > 0 ? coachSeatCombination.join(', ') : undefined,
        },
        origin: {
          date: leg?.departAtLocal,
          stationCode: leg.originInfo?.sourceRefInfos?.find((info) => info.inventoryName === 'Amtrak')
            ? leg.originInfo?.code
            : '',
          city: leg.originInfo?.cityName,
          name: leg.originInfo?.name,
          countryCode: leg.originInfo?.countryCode,
        },
        destination: {
          date: leg?.arriveAtLocal,
          stationCode: leg.destinationInfo?.sourceRefInfos?.find((info) => info.inventoryName === 'Amtrak')
            ? leg.destinationInfo?.code
            : '',
          city: leg.destinationInfo?.cityName,
          name: leg.destinationInfo?.name,
          countryCode: leg.destinationInfo?.countryCode,
        },
        duration: minutesToDurationString(
          getDateTimeDiff(leg?.arriveAtLocal?.iso8601 ?? '', leg?.departAtLocal?.iso8601 ?? ''),
        ),
        layover,
        passengerDetails,
        vendorName: leg.vendorName,
        isOutOfPolicy: this.isPnrOutOfPolicy(),
      };
    });

    const hasPreviousInwardJourney = !!previousItinerary?.inwardJourney;

    return {
      doesPreviousItineraryExist: !!previousItinerary,
      previousRailDetails,
      sourceConfirmation: previousItinerary?.sourceReference,
      exchangeType: exchangeInfo?.exchangeType,
      searchType: previousItinerary?.type,
      deliveryOption: previousItinerary?.deliveryOption,
      rate: previousItinerary?.rate,
      hasPreviousInwardJourney,
      previousOutwardJourneyInfo,
      previousInwardJourneyInfo,
    };
  }

  public getTotalCo2EmissionsInKg() {
    const outwardCo2 = this.railPnr?.outwardJourney.co2EmissionDetails?.co2EmissionKilograms ?? 0;
    const inwardCo2 = this.railPnr?.inwardJourney?.co2EmissionDetails?.co2EmissionKilograms ?? 0;
    const inStr = inwardCo2 !== 0 ? inwardCo2.toFixed(1) : '0';
    const outStr = outwardCo2 !== 0 ? outwardCo2.toFixed(1) : '0';
    const totalCo2Emissions = Number(inStr) + Number(outStr);
    return totalCo2Emissions;
  }

  public splitFaresPerPax() {
    const numTravelers = this.pnrData?.pnrTravelers?.length || 1;
    const fares: Fare[] = [];
    const { baseAmount, taxAmount } = super.paymentInfo();
    const splitBaseAmount = baseAmount.divide(numTravelers);
    const splitTaxAmount = taxAmount.divide(numTravelers);
    for (let i = 0; i < numTravelers; i += 1) {
      fares.push({ base: splitBaseAmount, tax: splitTaxAmount });
    }
    return fares;
  }

  public paymentInfo() {
    const transactionDate = first(this.pnrData?.bookingHistory)?.bookingInfo?.updatedDateTime;
    const isDelayedInvoicing = this.pnrData?.invoiceDelayedBooking || false;
    const refundAmount = this.railPnr?.rate?.refundInfo?.refundAmount;
    const refundAmountTotal = MoneyUtil.convertV2MoneyToMoneyUtil(refundAmount?.base).add(
      MoneyUtil.convertV2MoneyToMoneyUtil(refundAmount?.tax),
    );
    const voucherAmount = MoneyUtil.convertV2MoneyToMoneyUtil(this.railPnr?.rate?.refundInfo?.refundVoucher?.amount);
    const cancelationFeeAmount = this.railPnr?.rate?.refundInfo?.penalty;
    const cancelationFee = MoneyUtil.convertV2MoneyToMoneyUtil(cancelationFeeAmount?.base).add(
      MoneyUtil.convertV2MoneyToMoneyUtil(cancelationFeeAmount?.tax),
    );

    const { paymentMethods, ...additionalPaymentInfo } = super.paymentInfo();
    const collectionReferenceId =
      this.pnrData.railPnr?.ticketDetails?.find(
        (ticket) => ticket.eticketMetadata?.type === ETicketMetadataTypeEnum.CollectionNumber,
      )?.reference ?? '';
    const paymentCard = paymentMethods.find((payment) => !!payment.name)?.name;
    const travelerNames = this.travelerDetails()?.map((traveler) => traveler.name) || [];

    const fares = this.splitFaresPerPax();

    const appliedPromotions: RailAppliedPromotion[] =
      this?.railPnr?.sections?.flatMap((section) => section?.fares?.flatMap((fare) => fare?.appliedPromotions || [])) ??
      [];

    return {
      ...additionalPaymentInfo,
      paymentMethods,
      transactionDate,
      isDelayedInvoicing,
      refundAmount: refundAmountTotal,
      voucherAmount,
      cancelationFee,
      collectionReferenceId,
      paymentCard,
      travelerNames,
      appliedPromotions,
      fares,
    };
  }

  public termsAndConditions() {
    return first(this.pnrData.railPnr?.termsAndConditions?.conditions);
  }

  public pnrTitle() {
    const { inwardJourneyInfo, outwardJourneyInfo, hasInwardJourney } = this.railDetailsAfterSplit();
    const journey = hasInwardJourney ? inwardJourneyInfo : outwardJourneyInfo;

    const pnrTitle = defineCommonMessage('Train to {{destination}}');
    pnrTitle.values = {
      destination: journey.destinationName ?? '',
    };
    return pnrTitle;
  }
}
